import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Trash2, Star, Upload, ImagePlus, Pencil, X, RotateCcw } from 'lucide-react';
import { WorkCard, WorkModal, imgUrl as showcaseImg } from '../../components/ShowcaseCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { brand: '', model: '', year: '', engine: '', software: '', description: '', ecu_type: '', sw: '', hw: '', tool: '', catalog_brand: '', catalog_model: '', catalog_year: '', catalog_engine: '', hp_before: '', hp_after: '', nm_before: '', nm_after: '' };

export default function AdminShowcasePage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [works, setWorks] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [chart, setChart] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [removePhotoIds, setRemovePhotoIds] = useState([]);
  const [existingChart, setExistingChart] = useState(null);
  const [removeChart, setRemoveChart] = useState(false);
  const [catBrands, setCatBrands] = useState([]);
  const [catModels, setCatModels] = useState([]);
  const [catYears, setCatYears] = useState([]);
  const [catEngines, setCatEngines] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/showcase`);
      setWorks(res.data.works || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/catalog/brands`).then(r => setCatBrands(r.data.brands || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!form.catalog_brand) { setCatModels([]); return; }
    axios.get(`${API}/catalog/models?brand=${encodeURIComponent(form.catalog_brand)}`)
      .then(r => setCatModels(r.data.models || [])).catch(() => {});
  }, [form.catalog_brand]);
  useEffect(() => {
    if (!form.catalog_brand || !form.catalog_model) { setCatYears([]); return; }
    axios.get(`${API}/catalog/years?brand=${encodeURIComponent(form.catalog_brand)}&model=${encodeURIComponent(form.catalog_model)}`)
      .then(r => setCatYears(r.data.years || [])).catch(() => {});
  }, [form.catalog_brand, form.catalog_model]);
  useEffect(() => {
    if (!form.catalog_brand || !form.catalog_model) { setCatEngines([]); return; }
    const p = new URLSearchParams({ brand: form.catalog_brand, model: form.catalog_model });
    if (form.catalog_year) p.append('year', form.catalog_year);
    axios.get(`${API}/catalog/engines?${p}`)
      .then(r => setCatEngines([...new Set((r.data.engines || []).map(e => e.engine))])).catch(() => {});
  }, [form.catalog_brand, form.catalog_model, form.catalog_year]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const resetForm = () => {
    setForm(EMPTY); setPhotos([]); setChart(null);
    setEditingId(null); setExistingPhotos([]); setRemovePhotoIds([]);
    setExistingChart(null); setRemoveChart(false);
  };

  const startEdit = (w) => {
    setEditingId(w.id);
    setForm({
      brand: w.brand || '', model: w.model || '', year: w.year || '', engine: w.engine || '',
      software: w.software || '', description: w.description || '',
      ecu_type: w.ecu_type || '', sw: w.sw || '', hw: w.hw || '', tool: w.tool || '',
      catalog_brand: w.catalog_brand || '', catalog_model: w.catalog_model || '',
      catalog_year: w.catalog_year || '', catalog_engine: w.catalog_engine || '',
      hp_before: w.hp_before ?? '', hp_after: w.hp_after ?? '',
      nm_before: w.nm_before ?? '', nm_after: w.nm_after ?? '',
    });
    setExistingPhotos(w.photo_ids || []);
    setExistingChart(w.chart_id || null);
    setPhotos([]); setChart(null); setRemovePhotoIds([]); setRemoveChart(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async () => {
    if (!form.brand || !form.model || !form.software) return toast.error('Marca, modelo y software son obligatorios');
    const remainingExisting = existingPhotos.filter(id => !removePhotoIds.includes(id));
    if (!editingId && photos.length === 0) return toast.error('Sube al menos una foto del coche');
    if (editingId && remainingExisting.length === 0 && photos.length === 0) return toast.error('El trabajo debe tener al menos una foto');
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      photos.forEach(p => fd.append('photos', p));
      if (chart) fd.append('chart', chart);
      if (editingId) {
        if (removePhotoIds.length) fd.append('remove_photo_ids', removePhotoIds.join(','));
        if (removeChart) fd.append('remove_chart', 'true');
        await axios.put(`${API}/admin/showcase/${editingId}`, fd, { headers, withCredentials: true });
        toast.success('Trabajo actualizado');
      } else {
        await axios.post(`${API}/admin/showcase`, fd, { headers, withCredentials: true });
        toast.success('Trabajo publicado');
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const toggleFeatured = async (w) => {
    try {
      await axios.put(`${API}/admin/showcase/${w.id}/featured`, {}, { headers, withCredentials: true });
      load();
    } catch { toast.error('Error'); }
  };

  const remove = async (w) => {
    if (!window.confirm(`¿Eliminar ${w.brand} ${w.model}?`)) return;
    try {
      await axios.delete(`${API}/admin/showcase/${w.id}`, { headers, withCredentials: true });
      toast.success('Eliminado');
      load();
    } catch { toast.error('Error al eliminar'); }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <h1 className="font-heading text-3xl font-bold text-white mb-1">TRABAJOS REALIZADOS</h1>
        <p className="text-zinc-400 mb-8">Publica fotos, gráficas y software — visibles para futuros clientes en la web</p>

        <div className="bg-surface border border-white/10 rounded-md p-6 mb-10">
          <h2 className="font-heading text-lg text-white mb-4">{editingId ? `EDITAR: ${form.brand} ${form.model}` : 'NUEVO TRABAJO'}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            <div><Label className="text-zinc-400 text-xs">Marca *</Label>
              <Input value={form.brand} onChange={set('brand')} placeholder="BMW" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-brand" /></div>
            <div><Label className="text-zinc-400 text-xs">Modelo *</Label>
              <Input value={form.model} onChange={set('model')} placeholder="330d" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-model" /></div>
            <div><Label className="text-zinc-400 text-xs">Año</Label>
              <Input value={form.year} onChange={set('year')} placeholder="2019" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-year" /></div>
            <div className="col-span-2 sm:col-span-3"><Label className="text-zinc-400 text-xs">Motor</Label>
              <Input value={form.engine} onChange={set('engine')} placeholder="2.0 TSi DNU" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-engine" /></div>
          </div>
          <div className="mb-3"><Label className="text-zinc-400 text-xs">Software realizado *</Label>
            <Input value={form.software} onChange={set('software')} placeholder="Stage 1 + EGR Off" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-software" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div><Label className="text-zinc-400 text-xs">Tipo de ECU</Label>
              <Input value={form.ecu_type} onChange={set('ecu_type')} placeholder="EDC17C64" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-ecu-type" /></div>
            <div><Label className="text-zinc-400 text-xs">SW</Label>
              <Input value={form.sw} onChange={set('sw')} placeholder="03L906012" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-sw" /></div>
            <div><Label className="text-zinc-400 text-xs">HW</Label>
              <Input value={form.hw} onChange={set('hw')} placeholder="03L907309AB" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-hw" /></div>
            <div><Label className="text-zinc-400 text-xs">Máquina utilizada</Label>
              <Input value={form.tool} onChange={set('tool')} placeholder="Magic FLEX" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-tool" /></div>
          </div>

          {/* Asociar al coche del buscador (catálogo) */}
          <div className="bg-surface-highlight/50 border border-primary/20 rounded-md p-3 mb-3">
            <Label className="text-primary text-xs font-medium">Asociar al buscador de la web (opcional)</Label>
            <p className="text-zinc-500 text-xs mb-2">Elige el coche del catálogo para que este trabajo salga cuando alguien lo busque en "Busca tu reprogramación".</p>
            <div className="grid grid-cols-2 gap-3">
              <select value={form.catalog_brand}
                onChange={(e) => setForm({ ...form, catalog_brand: e.target.value, catalog_model: '', catalog_year: '', catalog_engine: '' })}
                className="bg-surface border border-white/10 rounded px-3 py-2 text-white text-sm" data-testid="showcase-catalog-brand">
                <option value="">Marca del catálogo…</option>
                {catBrands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={form.catalog_model} disabled={!form.catalog_brand}
                onChange={(e) => setForm({ ...form, catalog_model: e.target.value, catalog_year: '', catalog_engine: '' })}
                className="bg-surface border border-white/10 rounded px-3 py-2 text-white text-sm disabled:opacity-40" data-testid="showcase-catalog-model">
                <option value="">Modelo del catálogo…</option>
                {catModels.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={form.catalog_year} disabled={!form.catalog_model}
                onChange={(e) => setForm({ ...form, catalog_year: e.target.value, catalog_engine: '' })}
                className="bg-surface border border-white/10 rounded px-3 py-2 text-white text-sm disabled:opacity-40" data-testid="showcase-catalog-year">
                <option value="">Año (opcional)…</option>
                {catYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select value={form.catalog_engine} disabled={!form.catalog_model} onChange={set('catalog_engine')}
                className="bg-surface border border-white/10 rounded px-3 py-2 text-white text-sm disabled:opacity-40" data-testid="showcase-catalog-engine">
                <option value="">Motor (opcional)…</option>
                {catEngines.map(en => <option key={en} value={en}>{en}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3 mb-3">
            <div><Label className="text-zinc-400 text-xs">CV antes</Label>
              <Input type="number" value={form.hp_before} onChange={set('hp_before')} placeholder="184" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-hp-before" /></div>
            <div><Label className="text-zinc-400 text-xs">CV después</Label>
              <Input type="number" value={form.hp_after} onChange={set('hp_after')} placeholder="230" className="bg-surface-highlight border-white/10 text-white" data-testid="showcase-hp-after" /></div>
            <div><Label className="text-zinc-400 text-xs">Nm antes</Label>
              <Input type="number" value={form.nm_before} onChange={set('nm_before')} placeholder="380" className="bg-surface-highlight border-white/10 text-white" /></div>
            <div><Label className="text-zinc-400 text-xs">Nm después</Label>
              <Input type="number" value={form.nm_after} onChange={set('nm_after')} placeholder="460" className="bg-surface-highlight border-white/10 text-white" /></div>
          </div>
          <div className="mb-3"><Label className="text-zinc-400 text-xs">Descripción (opcional)</Label>
            <Textarea value={form.description} onChange={set('description')} className="bg-surface-highlight border-white/10 text-white min-h-[60px]" /></div>

          {editingId && existingPhotos.length > 0 && (
            <div className="mb-4">
              <Label className="text-zinc-400 text-xs">Fotos actuales (pulsa la ✕ para quitar)</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {existingPhotos.map(pid => {
                  const marked = removePhotoIds.includes(pid);
                  return (
                    <div key={pid} className="relative">
                      <img src={showcaseImg(pid, 'thumb')} alt="" className={`w-20 h-20 object-cover rounded border ${marked ? 'border-red-500 opacity-40' : 'border-white/10'}`} />
                      <button type="button" onClick={() => setRemovePhotoIds(p => marked ? p.filter(x => x !== pid) : [...p, pid])}
                        className={`absolute -top-2 -right-2 p-0.5 rounded-full ${marked ? 'bg-zinc-600' : 'bg-red-600'} text-white`}
                        data-testid={`edit-remove-photo-${pid}`}>
                        {marked ? <RotateCcw className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {editingId && existingChart && !removeChart && (
            <div className="mb-4">
              <Label className="text-zinc-400 text-xs">Gráfica actual</Label>
              <div className="relative inline-block mt-1">
                <img src={showcaseImg(existingChart)} alt="" className="w-28 h-20 object-cover rounded border border-white/10 bg-white" />
                <button type="button" onClick={() => setRemoveChart(true)}
                  className="absolute -top-2 -right-2 p-0.5 rounded-full bg-red-600 text-white" data-testid="edit-remove-chart">
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <label className="flex flex-col items-center gap-1 p-4 border-2 border-dashed border-white/10 rounded-md cursor-pointer hover:border-primary/50">
              <ImagePlus className="w-6 h-6 text-zinc-500" />
              <span className="text-zinc-400 text-sm">{photos.length > 0 ? `${photos.length} foto(s) nueva(s)` : (editingId ? 'Añadir más fotos' : 'Fotos del coche (varias)')}</span>
              <input type="file" accept="image/*" multiple className="hidden" data-testid="showcase-photos-input"
                onChange={e => setPhotos(Array.from(e.target.files).slice(0, 8))} />
            </label>
            <label className="flex flex-col items-center gap-1 p-4 border-2 border-dashed border-white/10 rounded-md cursor-pointer hover:border-primary/50">
              <Upload className="w-6 h-6 text-zinc-500" />
              <span className="text-zinc-400 text-sm">{chart ? chart.name : (editingId ? 'Reemplazar gráfica' : 'Gráfica de potencia (1 imagen)')}</span>
              <input type="file" accept="image/*" className="hidden" data-testid="showcase-chart-input"
                onChange={e => setChart(e.target.files[0] || null)} />
            </label>
          </div>
          <div className="flex gap-3">
            <Button onClick={submit} disabled={saving} className="bg-primary hover:bg-primary/90 flex-1" data-testid="showcase-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {editingId ? 'Guardar cambios' : 'Publicar Trabajo'}
            </Button>
            {editingId && (
              <Button onClick={resetForm} variant="outline" className="border-white/10 text-zinc-300" data-testid="showcase-cancel-edit">
                Cancelar
              </Button>
            )}
          </div>
        </div>

        <h2 className="font-heading text-lg text-white mb-4">PUBLICADOS ({works.length})</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {works.map(w => (
            <div key={w.id} className="relative">
              <WorkCard work={w} onClick={() => setSelected(w)} />
              <div className="absolute top-2 left-2 flex gap-1">
                <button onClick={() => toggleFeatured(w)} title="Destacado en portada"
                  className={`p-1.5 rounded ${w.featured ? 'bg-amber-500 text-black' : 'bg-black/60 text-zinc-400 hover:text-amber-400'}`}
                  data-testid={`showcase-feature-${w.id}`}>
                  <Star className="w-4 h-4" />
                </button>
                <button onClick={() => startEdit(w)} title="Editar" className="p-1.5 rounded bg-black/60 text-zinc-400 hover:text-primary"
                  data-testid={`showcase-edit-${w.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => remove(w)} className="p-1.5 rounded bg-black/60 text-zinc-400 hover:text-red-400"
                  data-testid={`showcase-delete-${w.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <WorkModal work={selected} onClose={() => setSelected(null)} />
    </Layout>
  );
}
